/**
 * @module api/download
 *
 * Provides functionality to download a cached episode file to the
 * user's device. The server redirects to a presigned R2 URL with
 * Content-Disposition: attachment, so the browser handles the
 * download natively via window.open.
 */

import { api } from "./config.js";

/**
 * Initiate a file download for a given episode.
 * Determines the file extension based on the episode type (audio = mp3,
 * video = mp4), then opens the server's play URL with `?download=true`
 * in a new browser tab. The server responds with a 302 redirect to
 * a presigned R2 URL that triggers a browser download.
 *
 * @param {Object} episode - The episode object to download
 * @param {string} episode.episode_type - Either "audio" or "video", determines file extension
 * @param {string} episode.video_id - Unique video identifier used in the download URL
 * @param {Object} snackbar - Reactive snackbar object for showing status messages
 * @param {boolean} snackbar.show - Whether the snackbar is visible
 * @param {string} snackbar.color - Snackbar color theme ("info", "error", etc.)
 * @param {string} snackbar.message - Message text displayed in the snackbar
 */
export const downloadMedia = async (episode, snackbar) => {
  const ext = episode.episode_type === "audio" ? "mp3" : "mp4";
  try {
    snackbar.show = true;
    snackbar.color = "info";
    snackbar.message = `Download started`;

    // Open the download URL directly - the server redirects to R2 presigned URL
    // with Content-Disposition: attachment, so the browser handles the download natively
    const baseUrl = api.defaults.baseURL || import.meta.env.VITE_API_BASE_URL;
    const url = `${baseUrl}/play/${episode.video_id}.${ext}?download=true`;
    window.open(url, "_blank");
  } catch (error) {
    snackbar.show = true;
    snackbar.color = "error";
    snackbar.message = `Error downloading episode: ${error}`;
    console.error("Error downloading episode:", error);
  }
};
